import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  FlashOn as FlashOnIcon,
  FlashOff as FlashOffIcon
} from '@mui/icons-material';

const BarcodeScanner = ({ open, onClose, onScan }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);

  // Função para iniciar a câmera
  const startCamera = async () => {
    try {
      setError(null);
      
      const constraints = {
        video: {
          facingMode: 'environment', // Câmera traseira
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }

      // Verificar se tem flash
      const track = mediaStream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();
      setHasFlash(capabilities.torch === true);
      
      setIsScanning(true);
    } catch (err) {
      console.error('Erro ao acessar câmera:', err);
      setError('Não foi possível acessar a câmera. Verifique as permissões.');
    }
  };

  // Função para parar a câmera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsScanning(false);
    setFlashOn(false);
  };

  // Função para alternar flash
  const toggleFlash = async () => {
    if (stream && hasFlash) {
      const track = stream.getVideoTracks()[0];
      try {
        await track.applyConstraints({
          advanced: [{ torch: !flashOn }]
        });
        setFlashOn(!flashOn);
      } catch (err) {
        console.error('Erro ao controlar flash:', err);
      }
    }
  };

  // Função para capturar e processar imagem
  const captureAndProcess = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Definir tamanho do canvas
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Desenhar frame do vídeo no canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Simular leitura de código de barras
    // Em uma implementação real, você usaria uma biblioteca como ZXing
    // Para este exemplo, vamos simular com um delay
    const mockBarcodeData = generateMockBarcode();
    
    if (mockBarcodeData) {
      onScan(mockBarcodeData);
      handleClose();
    }
  };

  // Gerar código de barras mock para demonstração
  const generateMockBarcode = () => {
    // Simular diferentes tipos de códigos que podem ser encontrados
    const mockCodes = [
      '35200114200166000166550010000000271023456789', // Chave NFe
      '12345678901234567890123456789012345678901234', // Chave genérica
      'PED-001-2024', // Código de pedido
      'BR123456789BR' // Código de rastreamento
    ];
    
    // Retornar um código aleatório para simular a leitura
    return mockCodes[Math.floor(Math.random() * mockCodes.length)];
  };

  // Função para fechar o scanner
  const handleClose = () => {
    stopCamera();
    onClose();
  };

  // Iniciar scanner quando abrir
  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [open]);

  // Verificar se é mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  if (!isMobile) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Scanner de Código de Barras
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">
            O scanner de código de barras está disponível apenas em dispositivos móveis.
            Em desktop, digite manualmente a chave da NFe.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: '#000',
          color: '#fff'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: '#fff',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000
      }}>
        <Typography variant="h6">Scanner de Código</Typography>
        <Box>
          {hasFlash && (
            <IconButton 
              onClick={toggleFlash}
              sx={{ color: '#fff', mr: 1 }}
            >
              {flashOn ? <FlashOnIcon /> : <FlashOffIcon />}
            </IconButton>
          )}
          <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden' }}>
        {error ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100vh',
            p: 3
          }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button 
              variant="contained" 
              onClick={startCamera}
              startIcon={<CameraIcon />}
            >
              Tentar Novamente
            </Button>
          </Box>
        ) : (
          <Box sx={{ position: 'relative', width: '100%', height: '100vh' }}>
            {/* Vídeo da câmera */}
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              playsInline
              muted
            />
            
            {/* Canvas oculto para captura */}
            <canvas
              ref={canvasRef}
              style={{ display: 'none' }}
            />

            {/* Overlay de escaneamento */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.5) 100%)',
              pointerEvents: 'none'
            }}>
              {/* Área de escaneamento */}
              <Box sx={{
                width: '80%',
                height: '200px',
                border: '2px solid #fff',
                borderRadius: 2,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(90deg, transparent, #ff4444, transparent)',
                  animation: 'scan 2s ease-in-out infinite',
                },
                '@keyframes scan': {
                  '0%': { transform: 'translateY(-100px)' },
                  '100%': { transform: 'translateY(100px)' }
                }
              }} />
            </Box>

            {/* Instruções */}
            <Box sx={{
              position: 'absolute',
              bottom: 120,
              left: 0,
              right: 0,
              textAlign: 'center',
              p: 2
            }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
                Aponte para o código de barras
              </Typography>
              <Typography variant="body2" sx={{ color: '#fff', opacity: 0.8 }}>
                Posicione o código dentro da área marcada
              </Typography>
            </Box>

            {/* Botão de captura manual */}
            <Box sx={{
              position: 'absolute',
              bottom: 30,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Button
                variant="contained"
                size="large"
                onClick={captureAndProcess}
                sx={{
                  backgroundColor: '#fff',
                  color: '#000',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#f0f0f0'
                  }
                }}
              >
                Capturar
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;